import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { EcondosQuery } from '@api/model/query';
import { WhiteLabel } from '@api/model/white-label';
import { WhiteLabelService } from '@api/service/white-label.service';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ClipboardService } from 'ngx-clipboard';
import { UserService } from '@api/service/user.service';
import { PaginationService } from '../../../service/pagination.service';

@Component({
  selector: 'app-white-labels-page',
  templateUrl: './white-labels-page.component.html',
  styleUrls: ['./white-labels-page.component.scss']
})
export class WhiteLabelsPageComponent implements OnInit, OnDestroy {
  private stateKey = 'whiteLabels-page';

  pageState$ = this.paginationService.getState(this.stateKey)?.pipe(
    switchMap(query => {
      if (!this.searchByName.value && !!query.companyName?.$regex) {
        this.searchByName.setValue(query.companyName.$regex, { emitEvent: false });
      }

      if (!this.searchByStatus.value.length && !!query.status?.$in) {
        this.searchByStatus.setValue(query.status.$in, { emitEvent: false });
      }

      return this.getWhiteLabels(query).pipe(
        map(({ whiteLabels, count }) => {
          return { whiteLabels, count, page: query.$page ? query.$page + 1 : 1, pageSize: query.$limit };
        })
      );
    })
  );

  searchByName = new UntypedFormControl('');
  searchByStatus = new UntypedFormControl([]);
  unsubscribe: Subject<boolean> = new Subject();

  listOfStatus: Array<{ label: string; value: string }> = [];

  statusKeys: any = {
    NEW: 'Novo',
    AWAITING_RESOURCES: 'Aguardando recursos',
    IN_REVIEW: 'Em revisão',
    DONE: 'Criado'
  };

  updateWhiteLabelStatusModalIsVisible = false;
  sendTestEmailIsModalIsVisible = false;

  newStatus = '';
  newObs = '';

  whitelabelToUpdate: WhiteLabel | null = null;
  emailSendToTest = '';

  constructor(
    private whitelabelService: WhiteLabelService,
    private notificationService: NzNotificationService,
    private messageService: NzMessageService,
    private clipboard: ClipboardService,
    private userService: UserService,
    private paginationService: PaginationService
  ) {
    this.searchByName.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.unsubscribe)).subscribe(name => {
      const query = this.paginationService.getCurrentQuery(this.stateKey);
      if (name) {
        query.$or = [
          {
            companyName: { $regex: name, $options: 'i' }
          },
          {
            name: { $regex: name, $options: 'i' }
          }
        ];
      } else {
        delete query.companyName;
      }
      query.$page = 0;

      this.paginationService.setState(this.stateKey, query);
    });

    this.searchByStatus.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.unsubscribe)).subscribe(status => {
      const query = this.paginationService.getCurrentQuery(this.stateKey);
      if (status) {
        query.status = { $in: status };
      } else {
        delete query.status;
      }
      query.$page = 0;

      this.paginationService.setState(this.stateKey, query);
    });
  }

  ngOnInit(): void {
    const statusTypes = [
      { value: 'NEW', label: 'Novo' },
      { value: 'AWAITING_RESOURCES', label: 'Aguardando recursos' },
      { value: 'IN_REVIEW', label: 'Em revisão' },
      { value: 'DONE', label: 'Criado' }
    ];
    this.listOfStatus = statusTypes;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  getWhiteLabels(customQuery: EcondosQuery = {}): Observable<{ whiteLabels: WhiteLabel[]; count: number }> {
    let $select = 'companyName webAppUrl icon status obs x86Installer x64Installer customerSupportUrl name';
    if (customQuery.$select) {
      $select += customQuery.$select;
    }

    return this.whitelabelService.getWithCount({ ...customQuery, $select });
  }

  handleUpdateStatusModalOk(): void {
    if (this.whitelabelToUpdate?._id && (this.newObs || this.newStatus)) {
      const loading = this.messageService.loading('Atualizando...').messageId;
      const update: any = {
        obs: this.newObs || '',
        status: this.newStatus || this.whitelabelToUpdate.status || ''
      };
      this.whitelabelService.update(this.whitelabelToUpdate._id, update).subscribe({
        next: () => {
          this.messageService.remove(loading);
          this.updateWhiteLabelStatusModalIsVisible = false;

          this.whitelabelToUpdate!.status = update.status;
          this.whitelabelToUpdate!.obs = update.obs;
          this.notificationService.success('Status atualizado', 'O status do whitelabel selecionado foi atualizado');

          this.whitelabelToUpdate = null;
        },
        error: () => {
          this.messageService.remove(loading);
          this.notificationService.error('Erro ao atualizar', 'O status do whitelabel não foi atualizado');
        }
      });
    }
  }

  handleUpdateStatusModalCancel(): void {
    this.newObs = '';
    this.newStatus = '';
    this.updateWhiteLabelStatusModalIsVisible = false;
  }

  handleSendTestEmailModalOk(): void {
    if (this.whitelabelToUpdate?._id && this.emailSendToTest) {
      const loading = this.messageService.loading('Enviando e-mail de teste...').messageId;
      this.userService.resetUserWhiteLabelPassword(this.emailSendToTest, this.whitelabelToUpdate._id).subscribe({
        next: () => {
          this.messageService.remove(loading);
          this.sendTestEmailIsModalIsVisible = false;
          this.notificationService.success('E-mail enviado', 'Cheque sua caixa de entrada e verifique se chegou algum e-mail');

          this.whitelabelToUpdate = null;
        },
        error: () => {
          this.messageService.remove(loading);
          this.notificationService.error('Erro ao enviar e-mail', 'Tente novamente!');
        }
      });
    }
  }

  handleSendTestEmailModalCancel(): void {
    this.emailSendToTest = '';
    this.sendTestEmailIsModalIsVisible = false;
  }

  copyLink(value: string = ''): void {
    this.clipboard.copy(value);
    this.notificationService.success('Link copiada!', '');
  }

  refreshIndexChange(value: any) {
    const currentQuery = this.paginationService.getCurrentQuery(this.stateKey);
    this.paginationService.setState(this.stateKey, { ...currentQuery, $page: value - 1 });
  }

  refreshSizeChange(value: any) {
    const currentQuery = this.paginationService.getCurrentQuery(this.stateKey);
    this.paginationService.setState(this.stateKey, { ...currentQuery, $limit: value });
  }
}
